namespace $ {

	type $shm_hitalama_baza_replay_item = {
		seal: $giper_baza_unit_seal
		bytes: Uint8Array< ArrayBuffer >
		pass: $giper_baza_auth_pass
		pass_bytes: Uint8Array< ArrayBuffer >
		data: Uint8Array< ArrayBuffer >
		sign: Uint8Array< ArrayBuffer >
		auditor: $mol_crypto2_auditor
	}

	/** Verify a stable batch before the native CRDT audit, without replaying its crypto loop. */
	export class $shm_hitalama_baza_replay extends $mol_object {

		static plan( land: $giper_baza_land, units: readonly $giper_baza_unit[] ) {
			const passes = new Map< string, $giper_baza_auth_pass >()
			for( const unit of units ) if( unit instanceof $giper_baza_auth_pass ) passes.set( unit.hash().str, unit )
			const link = land.link()
			const items: $shm_hitalama_baza_replay_item[] = []
			for( const unit of units ) {
				if( !( unit instanceof $giper_baza_unit_seal ) || land.$.$giper_baza_unit_trusted_check( unit ) ) continue
				const pass = land.lord_pass( unit.lord() ) ?? passes.get( unit.lord().str )
				if( !pass ) $mol_fail( new Error( 'No Pass for Lord' ) )
				const mixed = unit.shot().mix( ( unit.wide() ? link.lord() : link ).toBin() )
				items.push({ seal: unit, bytes: unit.asArray().slice(), pass, pass_bytes: pass.asArray().slice(), data: mixed.slice(), sign: unit.sign().slice(), auditor: pass.auditor() })
			}
			return { link: link.str, items }
		}

		/** Native async boundary: no CRDT reads or writes after suspension. */
		static async verify( plan: ReturnType< typeof $shm_hitalama_baza_replay.plan > ) {
			let cursor = 0
			const errors: unknown[] = []
			const worker = async()=> {
				while( cursor < plan.items.length && !errors.length ) {
					const item = plan.items[ cursor++ ]
					try {
						if( !await item.auditor.verify( item.data, item.sign ) ) throw new Error( 'Wrong Sign' )
					} catch( error ) { errors.push( error ) }
				}
			}
			await Promise.all( Array.from({ length: Math.min( 8, plan.items.length ) }, worker ) )
			if( errors.length ) $mol_fail( errors[ 0 ] )
		}

		@ $mol_action
		static prepare( land: $giper_baza_land, units: readonly $giper_baza_unit[], skip_load?: 'skip_load' ) {
			if( !units.length ) return
			if( !skip_load ) land.loading()
			const plan = $mol_wire_sync( this ).plan( land, units )
			if( plan.items.length ) $mol_wire_sync( this ).verify( plan )
			if( land.link().str !== plan.link || plan.items.some( item =>
				!$mol_compare_deep( item.bytes, item.seal.asArray() ) || !$mol_compare_deep( item.pass_bytes, item.pass.asArray() )
			) ) {
				$mol_fail( new Error( 'Signature input changed during verification' ) )
			}
			for( const item of plan.items ) $giper_baza_unit_trusted_grant( item.seal )
		}

		@ $mol_action
		static apply( land: $giper_baza_land, units: readonly $giper_baza_unit[], skip_load?: 'skip_load' ) {
			if( !units.length ) return
			$mol_wire_sync( this ).prepare( land, units, skip_load )
			// Signature verification is complete; native rank/order/Pass/Seal audit stays mandatory.
			return land.diff_apply( units, 'skip_load' )
		}

	}

}
